import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "components/buttons";
import { Dropdown } from "components/dropdown";
import { Text } from "components/text";
import { TreeViewComponent } from "components/treeview";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";
import { IConnection, IFormProps, INestedSchema, IProjectProps } from "data/types";
import { SampleBrowser } from "components/sampleBrowser";
import { LabelMap } from "data/Sample";
import { useFavorites } from "hooks/useFavorites";
import { useProject } from "hooks/useProject";
import { NestedSchemaList } from "components/nestedSchemaList";
import { FavoriteStar } from "components/favoriteStar";

export function Project() {
    const [error, setError] = useState<string>();
    const [projects, setProjects] = useState<IProjectProps[]>([]);
    const location = useParams<{ projectId: string }>();
    const [projectId, setProjectId] = useState<string>(location.projectId,);
    const [forms, setForms] = useState<INestedSchema>();
    const [flattenedForms, setFlattenedForms] = useState<IFormProps[]>();
    const [samples, setSamples] = useState<LabelMap>();
    const [connections, setConnections] = useState<IConnection[]>([]);
    const [formsLoaded, setFormsLoaded] = useState<boolean>(false);
    const cartaObjectStorage = useCartaObjectStorage();
    const projectCache = useProject();
    const { isFavorite, toggleFavorite } = useFavorites();

    useEffect(() => {
        (async () => {
            try {
                if (!cartaObjectStorage) {
                    return;
                }

                const projectList = projectCache.getProjects();
                if (!projectList) {
                    // Not loaded yet; this effect re-runs once ProjectCacheProvider's
                    // startup fetch populates it.
                    return;
                }

                setFormsLoaded(false);
                setProjects(projectList);

                if (projectList.length === 1) setProjectId(projectList[0].projectId);
            } catch (error) {
                if (error instanceof Error) setError(error.message);
                else setError("An unknown error occurred.");
            }
        })();
    }, [cartaObjectStorage, projectCache]);

    useEffect(() => {
        (async () => {
            try {

                if (!cartaObjectStorage) {
                    return;
                }
                if (!projectId) return;

                const cached = projectCache.get(projectId);
                if (cached) {
                    setForms(cached.forms);
                    setFlattenedForms(cached.flatForms);
                    setConnections(cached.connections);
                    setSamples(cached.samples);
                    setFormsLoaded(true);
                    return;
                }

                // A different project is loading; drop whatever was cached for the last one.
                projectCache.clear();

                const forms = await cartaObjectStorage.listSchemas({ projectId });
                setForms(forms);
                setFormsLoaded(true);

                const connectionsResult = await cartaObjectStorage.listConnections({ projectId });
                setConnections(connectionsResult);

                const samples = await cartaObjectStorage.getSamples({
                    projectId: projectId,
                    connectionId: connectionsResult[0].connectionId,
                });
                setSamples(samples);

                const flattenForms = (level: INestedSchema, forms: IFormProps[])=>{
                    forms.push(...level.schema.map(s=>({title: s.name, key: s.key})))
                    level.children.forEach(c=>flattenForms(c.value, forms))
                }

                const flattenedSchema: IFormProps[] = []
                flattenForms(forms, flattenedSchema)
                setFlattenedForms(flattenedSchema)

                projectCache.set(projectId, { forms, connections: connectionsResult, samples, flatForms:  flattenedSchema});
            } catch (error) {
                if (error instanceof Error) setError(error.message);
                else setError("An unknown error occurred.");
            }
        })();
    }, [projectId, cartaObjectStorage, projectCache]);

    const handleExport = () => { };

    const project = projects.find(p => p.projectId === projectId);
    const favoritedForms = flattenedForms?.filter(f => isFavorite(f.key)) ?? [];

    const enabledButtonStyle = {
        color: "white",
        backgroundColor: "#6c757d",
        borderColor: "#6c757d",
        padding: ".375rem .75rem",
    };

    if (!projectId) {
        return (<>Invalid project</>)
    }


    if (!formsLoaded) {
        return (error ? (<Text color="error" size="small" padding="center"> {error} </Text>) : (<Text size="medium">Loading...</Text>))
    }

    return (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", flex: 1 }}>
            <div style={{ flex: 1, flexBasis: "50%" }}>
                <Text size="medium">Select Existing</Text>
                {connections.map((item: IConnection) => (
                    <TreeViewComponent
                        key={item.connectionId}
                        connectionId={item.connectionId}
                        node={item.node}
                        projectId={projectId}
                        forms={flattenedForms}
                    />
                ))}
                <Button onClick={handleExport} style={enabledButtonStyle} disabled>
                    Export All
                </Button>
                <Dropdown side="bottom-right">
                    <Dropdown.Toggler caret>
                        <Text size="medium">Start New</Text>
                    </Dropdown.Toggler>
                    <Dropdown.Area>
                        {favoritedForms.map((item: IFormProps) => {
                            const title = item.title || item.key.substring(0, item.key.lastIndexOf("."));
                            return (
                                <Dropdown.Item
                                    key={item.key}
                                    style={{ fontSize: "var(--font-small)" }}
                                    href={`/${projectId}/form?key=${item.key}`}
                                    icon={<FavoriteStar favorited={true} onClick={() => toggleFavorite({ key: item.key, title, projectId: projectId!, projectName: project?.name || '' })} />}
                                >
                                    {title}
                                </Dropdown.Item>
                            );
                        })}
                        {forms && (
                            <NestedSchemaList
                                schema={forms}
                                onClick={(leaf) => { window.location.href = `/${projectId}/form?key=${leaf.key}`; }}
                                renderLeafIcon={(leaf) => (
                                    <FavoriteStar
                                        favorited={isFavorite(leaf.key)}
                                        onClick={() => toggleFavorite({
                                            key: leaf.key,
                                            title: leaf.name,
                                            projectId: projectId!,
                                            projectName: project?.name || ''
                                        })}
                                    />
                                )}
                                isIconAlwaysVisible={(leaf) => isFavorite(leaf.key)}
                            />
                        )}
                    </Dropdown.Area>
                </Dropdown>
                <Text color="error" size="small" padding="center">
                    {error}
                </Text>
            </div>
            {samples && connections ? (
                <SampleBrowser
                    samples={samples}
                    connection={{ projectId, connectionId: connections[0].connectionId }}
                    style={{ flex: 1, flexBasis: "50%" }}></SampleBrowser>
            ) : (
                <></>
            )}
        </div>
    )
}

